<template>
  <div>
    <header-component />
    <div class="container-fluid mt-4">
      <div class="card">
        <div class="card-header">
          <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
            <h3 class="mb-0">
              Order List
              <span v-if="meta && meta.total" class="badge badge-danger ml-2" style="font-size:0.75rem;">
                {{ meta.total }} item{{ meta.total !== 1 ? 's' : '' }} below minimum
              </span>
            </h3>
            <select v-model="selectedBranchId" @change="onBranchChange" class="form-control form-control-sm" style="width:200px;">
              <option value="">Select Branch</option>
              <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
            </select>
          </div>
          <div class="d-flex flex-wrap gap-2 align-items-center">
            <input
              v-model="search"
              @input="debouncedFetch"
              type="text"
              placeholder="Search product..."
              class="form-control form-control-sm"
              style="width:160px;"
            />
            <el-select v-model="company" @change="onFilterChange" filterable clearable placeholder="Company" size="small" style="width:160px;">
              <el-option v-for="v in companyOptions" :key="v" :label="v" :value="v" />
            </el-select>
            <el-select v-model="brand" @change="onFilterChange" filterable clearable placeholder="Brand" size="small" style="width:150px;">
              <el-option v-for="v in brandOptions" :key="v" :label="v" :value="v" />
            </el-select>
            <el-select v-model="supplier" @change="onFilterChange" filterable clearable placeholder="Supplier" size="small" style="width:170px;">
              <el-option v-for="s in supplierOptions" :key="s.supplierId" :label="s.name" :value="s.name" />
            </el-select>
            <button v-if="hasFilters" @click="clearFilters" class="btn btn-sm btn-outline-secondary">Clear</button>
          </div>
        </div>

        <div v-if="!selectedBranchId" class="p-4 text-center text-muted">
          Select a branch to view the order list.
        </div>

        <div v-else>
          <table class="table table-sm table-flush">
            <thead class="thead-light">
              <tr>
                <th>Product</th>
                <th>Company</th>
                <th>Brand</th>
                <th>Supplier</th>
                <th style="width:110px;">Current Qty</th>
                <th style="width:100px;">Min Qty</th>
                <th style="width:100px;">Order Qty</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading">
                <td colspan="7" class="text-center py-3">Loading...</td>
              </tr>
              <tr v-else-if="!items.length">
                <td colspan="7" class="text-center text-muted py-3">
                  {{ selectedBranchId ? 'All products are at or above minimum quantity.' : '' }}
                </td>
              </tr>
              <tr v-for="row in items" :key="row.product_id">
                <td>
                  <span class="font-weight-bold">{{ row.product_name }}</span>
                  <div class="text-muted" style="font-size:0.78em;">{{ row.barcode || '' }}</div>
                </td>
                <td class="text-muted" style="font-size:0.85em;">{{ row.company || '—' }}</td>
                <td class="text-muted" style="font-size:0.85em;">{{ row.brand || '—' }}</td>
                <td class="text-muted" style="font-size:0.85em;">{{ row.supplier_name || '—' }}</td>
                <td>
                  <span class="text-danger font-weight-bold">{{ Math.floor(row.quantity) }}</span>
                </td>
                <td class="text-muted">{{ row.minimum_quantity }}</td>
                <td>
                  <span class="text-primary font-weight-bold">
                    {{ row.order_quantity != null ? row.order_quantity : row.minimum_quantity - Math.floor(row.quantity) }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
          <div v-if="meta" class="card-footer">
            <div class="flex-wrap col-12 d-flex justify-content-center justify-content-sm-between">
              <p class="card-category">Showing {{ meta.from }} to {{ meta.to }} of {{ meta.total }} entries</p>
              <pagination :meta="meta" @paginate="onPaginate" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import headerComponent from "../Components/HeaderComponent";
import pagination from "@/components/AmsaPagination";
import { apiHelper } from "@/utils/helpers";
import endpoints from "@/utils/constants";
import { Select, Option } from "element-ui";

const LARAVEL = endpoints.LARAVEL_BASE_URL;
const NODE    = endpoints.BASE_URL;

export default {
  components: {
    "header-component": headerComponent,
    pagination,
    [Select.name]: Select,
    [Option.name]: Option,
  },
  data() {
    return {
      branches: [],
      selectedBranchId: "",
      search: "",
      company: "",
      brand: "",
      supplier: "",
      companyOptions: [],
      brandOptions: [],
      supplierOptions: [],
      items: [],
      meta: null,
      page: 1,
      loading: false,
      _fetchTimer: null,
    };
  },
  computed: {
    hasFilters() {
      return this.search || this.company || this.brand || this.supplier;
    },
  },
  mounted() {
    apiHelper.call("get", LARAVEL + "/api/v1/branches")
      .then(res => { this.branches = res.data.data || res.data; })
      .catch(() => {});

    apiHelper.call("get", LARAVEL + "/api/v1/branch-inventory/attribute-values")
      .then(res => {
        this.companyOptions = res.data.company || [];
        this.brandOptions   = res.data.brand   || [];
      })
      .catch(() => {});

    apiHelper.call("get", NODE + endpoints.API.suppliers)
      .then(res => { this.supplierOptions = res.data || []; })
      .catch(() => {});

    apiHelper.call("get", LARAVEL + "/api/v1/me")
      .then(res => {
        if (res.data && res.data.current_branch_id) {
          this.selectedBranchId = res.data.current_branch_id;
          this.fetchOrderList();
        }
      })
      .catch(() => {});
  },
  methods: {
    onBranchChange() {
      this.page = 1;
      this.fetchOrderList();
    },
    onFilterChange() {
      this.page = 1;
      this.fetchOrderList();
    },
    debouncedFetch() {
      clearTimeout(this._fetchTimer);
      this._fetchTimer = setTimeout(() => { this.page = 1; this.fetchOrderList(); }, 350);
    },
    clearFilters() {
      this.search   = "";
      this.company  = "";
      this.brand    = "";
      this.supplier = "";
      this.page     = 1;
      this.fetchOrderList();
    },
    onPaginate(data) {
      this.page = data.page;
      this.fetchOrderList();
    },
    fetchOrderList() {
      if (!this.selectedBranchId) return;
      this.loading = true;
      var p = new URLSearchParams({ branch_id: this.selectedBranchId, page: this.page, per_page: 50 });
      if (this.search)   p.append("search",   this.search);
      if (this.company)  p.append("company",  this.company);
      if (this.brand)    p.append("brand",    this.brand);
      if (this.supplier) p.append("supplier", this.supplier);
      apiHelper.call("get", LARAVEL + "/api/v1/branch-inventory/order-list?" + p.toString())
        .then(res => {
          this.items = res.data.items || [];
          this.meta  = res.data.meta  || null;
        })
        .catch(() => {})
        .finally(() => { this.loading = false; });
    },
  },
};
</script>
